import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, Download } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Card } from '@/shared/components/ui/card'
import { QueryErrorScreen } from '@/shared/components/ui/query-error-screen'
import { RefreshButton } from '@/shared/components/ui/refresh-button'
import { routes } from '@/shared/constants'
import { useMyBusinesses } from '@/features/businesses'
import { useBilling } from './hooks/useBilling'
import { useExportBillingPdf } from './hooks/useExportBillingPdf'
import { usePayBilling } from './hooks/usePayBilling'
import { PayBillingDialog } from '@/features/payments/components/PayBillingDialog'
import { BillingInvoiceCard } from './components/BillingInvoiceCard'
import { BillingDetailSkeleton } from './components/BillingSkeletons'
import { PAYABLE_STATUSES } from './constants/billingStatusFilters'

function BackLink() {
  return (
    <Link
      to={routes.app.billing.list}
      className="inline-flex items-center gap-1.5 text-sm text-ink-muted transition-colors hover:text-ink"
    >
      <ArrowLeft className="h-4 w-4" />
      Volver a Facturación
    </Link>
  )
}

export function BillingDetailPage() {
  const { id } = useParams<{ id: string }>()
  const billingId = Number(id)
  const valid = Number.isInteger(billingId) && billingId > 0

  const { billing, loading, error, refetch } = useBilling(valid ? billingId : undefined)
  const { businesses } = useMyBusinesses({ take: 50, skip: 0 })
  const { download, loading: pdfLoading } = useExportBillingPdf()
  const { pay, close, payBillingId } = usePayBilling()

  if (!valid) {
    return (
      <div className="space-y-6">
        <BackLink />
        <Card>
          <p className="text-sm text-ink-muted">Factura no válida.</p>
        </Card>
      </div>
    )
  }

  if (error) {
    return (
      <QueryErrorScreen
        error={error}
        onRetry={() => refetch()}
        title="No pudimos cargar la factura"
      />
    )
  }

  if (loading) {
    return <BillingDetailSkeleton />
  }

  if (!billing) {
    return (
      <div className="space-y-6">
        <BackLink />
        <Card>
          <p className="text-sm text-ink-muted">
            Factura no encontrada o no pertenece a tu cuenta.
          </p>
        </Card>
      </div>
    )
  }

  const businessName = billing.businessId
    ? businesses.find((b) => b.id === billing.businessId)?.legalName
    : undefined
  const payable = PAYABLE_STATUSES.has(billing.status?.toUpperCase() ?? '')

  return (
    <div className="space-y-6">
      <BackLink />

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-serif text-[32px] font-normal leading-[1.1] tracking-tight text-ink">
            Factura {billing.invoiceNumber}
          </h1>
          {businessName && <p className="mt-1 text-sm text-ink-muted">{businessName}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <RefreshButton onClick={async () => { await refetch() }} />
          {payable && (
            <Button variant="primary" onClick={() => pay(billing.id)}>
              Pagar
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => download(billing.id, billing.invoiceNumber)}
            disabled={pdfLoading}
          >
            <Download className="h-4 w-4" />
            {pdfLoading ? 'Generando…' : 'Descargar PDF'}
          </Button>
        </div>
      </header>

      <BillingInvoiceCard billing={billing} />

      <PayBillingDialog
        billingId={payBillingId}
        onClose={close}
        onPaid={() => {
          close()
          void refetch()
        }}
      />
    </div>
  )
}
